/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormLabel,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { Icon } from '@iconify/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { apipost } from '../../../service/api';

const DISPOSITIONS = ['Connected', 'Voicemail', 'No Answer', 'Wrong Number'];
const DURATIONS = ['< 1 min', '1-5 min', '5-15 min', '15-30 min', '30+ min'];

const CallDispositionDialog = ({ open, handleClose, leadId, leadData, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);

  const borrowerName = `${leadData?.firstName || ''} ${leadData?.lastName || ''}`.trim();
  const phoneNumber = leadData?.phoneNumber || '';

  const validationSchema = yup.object({
    disposition: yup.string().required('Disposition is required'),
    duration: yup.string().required('Duration is required'),
    note: yup.string().required('Note is required'),
  });

  const formik = useFormik({
    initialValues: {
      disposition: '',
      duration: '',
      note: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setSubmitting(true);

        // Create call record using existing /call/add endpoint
        const callPayload = {
          subject: `Call to ${borrowerName}`,
          status: values.disposition,
          startDateTime: dayjs().format('YYYY-MM-DDTHH:mm'),
          duration: values.duration,
          relatedTo: 'Lead',
          note: values.note,
          lead_id: leadId,
        };

        const callResult = await apipost('call/add', callPayload);

        // Auto follow-up: Create task if Voicemail or No Answer
        if (values.disposition === 'Voicemail' || values.disposition === 'No Answer') {
          const taskPayload = {
            subject: `Follow up call - ${borrowerName}`,
            status: 'Not Started',
            priority: 'High',
            startDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
            note: `Auto-created follow-up: ${values.disposition} on ${dayjs().format('MMM D, YYYY h:mm A')}. Previous note: ${values.note}`,
            lead_id: leadId,
            relatedTo: 'Lead',
          };

          await apipost('task/add', taskPayload);
          toast.info('Follow-up task created for tomorrow.');
        }

        if (callResult && callResult.status === 200) {
          resetForm();
          handleClose();
          if (onSuccess) onSuccess(callResult);
        }
      } catch (error) {
        console.error('Error logging call:', error);
        toast.error('Failed to log call.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDialogClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:phone" style={{ fontSize: 24, color: '#54D62C' }} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                Log Call
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {borrowerName} &middot; {phoneNumber}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleDialogClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Phone number action chips */}
        {phoneNumber && (
          <Box display="flex" gap={1} mb={3}>
            <Chip
              icon={<Icon icon="mdi:phone" />}
              label={`Call ${phoneNumber}`}
              component="a"
              href={`tel:${phoneNumber}`}
              clickable
              color="success"
              variant="outlined"
              size="small"
            />
            {leadData?.alternatePhoneNumber && (
              <Chip
                icon={<Icon icon="mdi:phone" />}
                label={`Alt: ${leadData.alternatePhoneNumber}`}
                component="a"
                href={`tel:${leadData.alternatePhoneNumber}`}
                clickable
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 5, md: 4 }}>
            {/* Disposition */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={formik.touched.disposition && Boolean(formik.errors.disposition)}>
                <FormLabel>Call Result</FormLabel>
                <Select
                  id="disposition"
                  name="disposition"
                  value={formik.values.disposition}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select result...
                  </MenuItem>
                  {DISPOSITIONS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.disposition && formik.errors.disposition && (
                  <Typography variant="caption" color="error">
                    {formik.errors.disposition}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Duration */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={formik.touched.duration && Boolean(formik.errors.duration)}>
                <FormLabel>Duration</FormLabel>
                <Select
                  id="duration"
                  name="duration"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select duration...
                  </MenuItem>
                  {DURATIONS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.duration && formik.errors.duration && (
                  <Typography variant="caption" color="error">
                    {formik.errors.duration}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <FormLabel>Note</FormLabel>
              <TextField
                id="note"
                name="note"
                size="small"
                fullWidth
                multiline
                rows={4}
                placeholder="What was discussed? Any follow-up needed?"
                value={formik.values.note}
                onChange={formik.handleChange}
                error={formik.touched.note && Boolean(formik.errors.note)}
                helperText={formik.touched.note && formik.errors.note}
              />
            </Grid>
          </Grid>

          {/* Auto follow-up notice */}
          {(formik.values.disposition === 'Voicemail' || formik.values.disposition === 'No Answer') && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: '#FFC10714',
                borderRadius: 1,
                border: '1px solid #FFC10740',
              }}
            >
              <Typography variant="caption" color="warning.dark" display="flex" alignItems="center" gap={0.5}>
                <Icon icon="mdi:information-outline" style={{ fontSize: 16 }} />
                A follow-up task will be automatically created for tomorrow.
              </Typography>
            </Box>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleDialogClose}
          variant="outlined"
          color="error"
          sx={{ textTransform: 'capitalize' }}
        >
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'capitalize' }}
        >
          {submitting ? 'Saving...' : 'Log Call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallDispositionDialog;
